import {
  useAuthStore,
  destinationTitle,
  useErrorMessage,
  leavesHost,
  openOnItsHost,
  toast,
} from '@sentinel/web-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { Destination } from '@sentinel/shared';
import { useTranslation } from 'react-i18next';
import { endpoints } from '@/core/api/endpoints';
import { grantFor, homeOf } from '@/core/auth/enter-destination';
import { keys } from '@/core/query/query-keys';

/** Where the signed-in identity can work, and the move from one context to another (phase D):
 *  the new grant replaces the session, the cache empties, the new context opens on its home. */
export function useContextSwitch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();
  const applyGrant = useAuthStore((s) => s.applyGrant);
  const list = useQuery({ queryKey: keys.destinations, queryFn: endpoints.auth.destinations });

  const enter = useMutation({
    mutationFn: async (destination: Destination) => {
      if (leavesHost(destination)) {
        openOnItsHost(destination);
        return null;
      }
      const me = useAuthStore.getState().me;
      return me === null ? null : grantFor(destination, me);
    },
    onSuccess: (grant, destination) => {
      if (grant === null) return;
      applyGrant(grant);
      toast('info', t('shell.context.switched', { name: destinationTitle(destination, t) }));
      void navigate({ to: homeOf(grant.me) });
    },
    onError: (error) => toast('att', errorMessage(error)),
  });

  const items = list.data?.items ?? [];
  return { items, current: items.find((d) => d.current), enter };
}
